import { Client, Event } from '@bugsnag/js';
import isNode from 'detect-node';
import {
  ApiError,
  MykissApolloError,
  MykissApolloNotFoundError,
  MykissGraphQLError,
} from '../errors';
import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { logger } from '../logging';

const getContext = (): string | undefined => {
  return isNode ? undefined : window.location.pathname;
};

export const log = (report: Event): void => {
  if (report.severity === 'error') {
    logger.error(report.originalError);
  } else if (report.severity === 'warning') {
    logger.warn(report.originalError);
  } else {
    logger.info(report.originalError);
  }
};

export const isNextRedirectError = (error: unknown): boolean => {
  // Check if the error is an object and has a 'digest' property
  return (
    typeof error === 'object' &&
    error !== null &&
    'digest' in error &&
    (error as { digest: string }).digest.startsWith('NEXT_REDIRECT')
  );
};

export const isApolloNotFoundError = (error: ApolloError): boolean => {
  return (
    error.message.startsWith(`GraphQL error: Couldn't find`) ||
    error.message.startsWith("Couldn't find `User` with given arguments") ||
    error.message.startsWith("Couldn't find `FishingWater` with given arguments") ||
    error.message.startsWith("Couldn't find `Place` with given arguments") ||
    error.message.startsWith("Couldn't find `Op::Post` with given arguments") ||
    error.message.startsWith("couldn't understand how to find `Op::Post` with id") ||
    error.message.startsWith("Couldn't find `Species` with given arguments") ||
    error.message.startsWith("couldn't understand how to find `Species` with id") ||
    error.message.startsWith("Couldn't find `Region` with `id`") ||
    error.message.startsWith("Couldn't find `Equipment::Product` with given arguments") ||
    error.message.startsWith("Couldn't find `Catch` with `ID`")
  );
};

const convertApolloError = (error: ApolloError): MykissApolloError | MykissApolloNotFoundError => {
  if (isApolloNotFoundError(error)) {
    return new MykissApolloNotFoundError(error.message, error);
  }
  return new MykissApolloError(error);
};

const getGraphQLGroupingHash = (message: string): string => {
  // This particular error contains ids and gets spread out in Bugsnag.
  if (message.startsWith("Couldn't find Taxon")) {
    return "Couldn't find Taxon";
  }

  // Bugsnag will group errors by surrounding code in the stack trace. For apollo errors that's
  // always code internal to the Apollo libraries, so all our GQL errors get grouped together.
  // To get better grouping we're instead just grouping based on the error message.
  return message;
};

export const reportAPIError = (
  bugsnagClient: Client,
  error: Response,
  severity?: 'info' | 'warning',
  otherData?: object,
  context: string | undefined = getContext(),
): void => {
  error
    .text()
    .then((errorText: string) => {
      bugsnagClient.notify(new ApiError(error, errorText), event => {
        event.addMetadata('response', {
          responseStatus: error.status,
          responseStatusText: error.statusText,
          responseErrorText: errorText,
          url: error.url,
          decodedUrl: decodeURI(error.url),
        });
        event.addMetadata('headers', { headers: error.headers });
        if (otherData) {
          event.addMetadata('debug', otherData);
        }

        event.context = context;

        event.severity = severity ?? 'error';

        event.groupingHash = `${context}-${error.status}-${errorText}`;
      });
    })
    .catch(bugsnagClient.notify);
};

export const reportApolloError = (
  bugsnagClient: Client,
  error: ApolloError,
  severity?: 'info' | 'warning',
  otherData?: object,
  context: string | undefined = getContext(),
): void => {
  const convertedError = convertApolloError(error);
  bugsnagClient.notify(convertedError, event => {
    event.addMetadata('ApolloError', {
      extraInfo: convertedError.extraInfo,
      graphQLErrors: convertedError.graphQLErrors,
      networkError: convertedError.networkError,
    });
    if (otherData) {
      event.addMetadata('debug', otherData);
    }

    event.context = context;

    if (convertedError.name === 'MykissApolloNotFoundError') {
      event.severity = severity ?? 'info';
    } else {
      event.severity = severity ?? 'error';
    }

    event.groupingHash = getGraphQLGroupingHash(convertedError.message);
  });
};

export const reportGraphQLError = (
  bugsnagClient: Client,
  error: GraphQLError,
  severity?: 'info' | 'warning',
  otherData?: object,
  context: string | undefined = getContext(),
): void => {
  const convertedError = new MykissGraphQLError(error);
  bugsnagClient.notify(convertedError, event => {
    event.addMetadata('MykissGraphQLError', {
      locations: convertedError.locations,
      path: convertedError.path,
      message: convertedError.message,
    });
    if (otherData) {
      event.addMetadata('debug', otherData);
    }

    event.context = context;

    event.severity = severity ?? 'error';

    event.groupingHash = getGraphQLGroupingHash(convertedError.message);
  });
};
